import { PointerActivationConstraints } from '@dnd-kit/dom';
import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { FC, useCallback, useMemo, useRef } from 'react';
import { cn, type QualityZoneRanges } from '@/shared/lib';
import type { ChartDropPlace } from '../../model/chartDrop';
import type { ChartAppearanceValues, PresetChartFormValues } from '../../model/dashboardPresetForm';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import { CHART_GRID_CLASS, chartGridRows, hasRoomForChart } from '../DashboardPresetGrid/chartLayout';
import type { CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';
import { AddChartTile } from './AddChartTile';
import { ChartDropIndicator } from './ChartDropIndicator';
import { ChartPropertiesBlock } from './ChartPropertiesBlock';
import { DraggedChartTitle } from './DraggedChartTitle';
import { EditableChartCard } from './EditableChartCard';

type Props = {
  charts: PresetChartFormValues[];
  /** Фильтры панели страницы — диаграммы правящегося дашборда наполняются по ним же */
  filters: CustomChartPanelFilters;
  /** Пороги зон качества пользователя: диаграммы окрашены ими и в режиме правки */
  zoneRanges: QualityZoneRanges;
  /** Диаграмма, свойства которой развёрнуты под её строкой; `null` — все свойства свёрнуты */
  expandedChartId: string | null;
  /** Завести диаграмму за диаграммой с этой позицией (`-1` — первой на пустой сетке) */
  onAddChart: (afterIndex: number, startsRow: boolean) => void;
  onToggleProperties: (chartId: string) => void;
  /** Введённое в блоке описание остаётся за диаграммой и после сворачивания блока */
  onKeepDescription: (chartId: string, description: string) => void;
  onResolveChart: (chart: PresetChartFormValues) => void;
  onApplyProperties: (chart: PresetChartFormValues) => void;
  onRemoveChart: (chartId: string) => void;
  /** Диаграмма отпущена над местом, показанным при перетаскивании */
  onDropChart: (chartId: string, place: ChartDropPlace) => void;
  onResizeChart: (chartId: string, change: ChartSizeChange) => void;
  onRestyleChart: (chartId: string, appearance: ChartAppearanceValues) => void;
  onRetemplateChart: (chartId: string, template: string | undefined) => void;
};

/** Порог в пикселях, после которого нажатие считается перетаскиванием, а не кликом по диаграмме. */
const DRAG_ACTIVATION_DISTANCE = 5;

/**
 * Сетка правящегося дашборда: та же раскладка, что у применённого пресета, но диаграммы
 * переставляются перетаскиванием, растягиваются границами и правятся своими свойствами.
 *
 * Место под новую диаграмму стоит в конце строки, где ещё осталась ширина под диаграмму, и
 * отдельной строкой в конце сетки — но только когда в последнюю строку диаграмма уже не
 * помещается: пока место в самой строке есть, отдельная строка была бы лишней.
 *
 * Блок свойств раскрывается не в ячейке своей диаграммы, а под всей её строкой во всю ширину
 * сетки: поля свойств не должны сжиматься до доли ширины показателя. Поэтому сетка складывается
 * строками, а не одним рядом ячеек, — блок встаёт за строкой, не уводя вниз ни соседок, ни место
 * под новую диаграмму.
 *
 * Перетаскивание задаёт не только место диаграммы в порядке, но и начало строки: отпущенная между
 * строками диаграмма открывает собой новую строку, а отпущенная в самой строке встаёт в неё наравне
 * с соседками. Куда она встанет, показано до отпускания ({@link ChartDropIndicator}); за курсором
 * идёт не сама диаграмма, а её название — карточка остаётся на месте, и сетка во время
 * перетаскивания не перестраивается.
 */
export const DashboardEditGrid: FC<Props> = ({
  charts,
  filters,
  zoneRanges,
  expandedChartId,
  onAddChart,
  onToggleProperties,
  onKeepDescription,
  onResolveChart,
  onApplyProperties,
  onRemoveChart,
  onDropChart,
  onResizeChart,
  onRestyleChart,
  onRetemplateChart,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const sensors = useMemo(
    () => [
      PointerSensor.configure({
        activationConstraints: [new PointerActivationConstraints.Distance({ value: DRAG_ACTIVATION_DISTANCE })],
      }),
    ],
    [],
  );

  // Ширина сетки нужна на момент растягивания — измеряем по требованию, а не подпиской на размер.
  const getGridWidth = useCallback(() => gridRef.current?.getBoundingClientRect().width ?? 0, []);

  const rows = useMemo(() => chartGridRows(charts), [charts]);
  // Заполненная до конца последняя строка оставляет состав без места под новую диаграмму —
  // тогда его и даёт отдельная строка в конце сетки.
  const isTrailingRowShown = rows.length > 0 && !hasRoomForChart(rows[rows.length - 1].freeColumns);

  // Ячейки сетки собираются одним рядом, а не обёрткой на строку: диаграмма, переехавшая при
  // растягивании в соседнюю строку, иначе пересобирала бы развёрнутый под ней блок свойств — и
  // начатая в нём правка терялась бы.
  const cells = rows.flatMap((row) => {
    const rowCells = row.chartIndexes.map((index) => (
      <EditableChartCard
        key={charts[index].chartId}
        chart={charts[index]}
        filters={filters}
        zoneRanges={zoneRanges}
        isPropertiesExpanded={expandedChartId === charts[index].chartId}
        getGridWidth={getGridWidth}
        // Действия — по идентификатору диаграммы, а не замыканиями на неё: ячейка перерисовывается
        // только правкой своей диаграммы, а не всякой правкой состава
        onToggleProperties={onToggleProperties}
        onRemove={onRemoveChart}
        onResize={onResizeChart}
      />
    ));

    if (hasRoomForChart(row.freeColumns)) {
      const lastIndex = row.chartIndexes[row.chartIndexes.length - 1];
      rowCells.push(
        <AddChartTile
          key={`add-after-${charts[lastIndex].chartId}`}
          startsRow={false}
          onAdd={() => onAddChart(lastIndex, false)}
        />,
      );
    }

    const expandedChart = row.chartIndexes
      .map((index) => charts[index])
      .find((chart) => chart.chartId === expandedChartId);
    if (expandedChart) {
      rowCells.push(
        <ChartPropertiesBlock
          key={`properties-${expandedChart.chartId}`}
          chart={expandedChart}
          filters={filters}
          // Ведущей может стать любая диаграмма дашборда, кроме самой правящейся: связь на себя
          // разбором не была бы.
          sourceCharts={charts
            .filter((item) => item.chartId !== expandedChart.chartId)
            .map((item) => ({ id: item.chartId, title: item.title }))}
          onResolve={onResolveChart}
          onKeepDescription={onKeepDescription}
          onResize={(change) => onResizeChart(expandedChart.chartId, change)}
          onRestyle={(appearance) => onRestyleChart(expandedChart.chartId, appearance)}
          onRetemplate={(template) => onRetemplateChart(expandedChart.chartId, template)}
          onSave={onApplyProperties}
          onCancel={() => onToggleProperties(expandedChart.chartId)}
        />,
      );
    }

    return rowCells;
  });

  return (
    <DragDropProvider sensors={sensors}>
      <div ref={gridRef} className={cn(CHART_GRID_CLASS, 'relative')}>
        {charts.length === 0 && <AddChartTile startsRow={false} onAdd={() => onAddChart(-1, false)} />}
        {cells}
        {isTrailingRowShown && <AddChartTile startsRow onAdd={() => onAddChart(charts.length - 1, true)} />}
        <ChartDropIndicator charts={charts} rows={rows} gridRef={gridRef} onDrop={onDropChart} />
      </div>
      {/* Возврата к прежнему месту нет: перенос применяется сразу по отпусканию */}
      <DragOverlay dropAnimation={null}>
        {(source) => (
          <DraggedChartTitle
            sourceElement={source.element}
            title={charts.find((chart) => chart.chartId === String(source.id))?.title}
          />
        )}
      </DragOverlay>
    </DragDropProvider>
  );
};
